import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useTeamId } from '@/hooks/useTeamId';
import { useQuery } from '@tanstack/react-query';

export interface TeamData {
  id: string;
  name: string;
  industry: string | null;
  business_structure: string | null;
  hq_location_city: string | null;
  hq_location_province: string | null;
  founded_year: number | null;
  annual_revenue: number | null;
  aum_total: number | null;
  num_advisors: number | null;
  num_employees: number | null;
  firm_valuation: number | null;
  firm_valuation_method: string | null;
  recurring_revenue_percentage: number | null;
  owner_dependency_percentage: number | null;
  client_count: number | null;
  updated_at: string | null;
  succession_target_date: string | null;
}

export interface ProfileData {
  company_name: string | null;
  industry: string | null;
  business_structure: string | null;
  years_in_business: number | null;
}

interface UseCompanyDataReturn {
  teamData: TeamData | null;
  profileData: ProfileData | null;
  companyName: string;
  loading: boolean;
  error: string | null;
}

export function useCompanyData(): UseCompanyDataReturn {
  const { user } = useAuth();
  const { effectiveTeamId, loading: teamLoading } = useTeamId();

  const {
    data: teamData = null,
    isLoading: teamQueryLoading,
    error: teamError,
  } = useQuery({
    queryKey: ['company-data', effectiveTeamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name, industry, business_structure, hq_location_city, hq_location_province, founded_year, annual_revenue, aum_total, num_advisors, num_employees, firm_valuation, firm_valuation_method, recurring_revenue_percentage, owner_dependency_percentage, client_count, updated_at, succession_target_date')
        .eq('id', effectiveTeamId!)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.warn('[useCompanyData] Team query error:', error.message);
        throw error;
      }

      return (data as TeamData | null) ?? null;
    },
    enabled: !!effectiveTeamId && !teamLoading,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: profileData = null,
    isLoading: profileQueryLoading,
  } = useQuery({
    queryKey: ['company-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('company_name, industry, business_structure, years_in_business')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.warn('[useCompanyData] Profile query error:', error.message);
      }

      return (data as ProfileData | null) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  const loading = teamLoading || teamQueryLoading || profileQueryLoading;
  const companyName = teamData?.name || profileData?.company_name || 'My Practice';

  return {
    teamData,
    profileData,
    companyName,
    loading,
    error: teamError ? (teamError as Error).message : null,
  };
}
